import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Typography,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VantageListHead from "../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  getComparator,
} from "../../../component/Methods";

const cellSx = (theme, align = "left") => ({
  fontSize: "12px",
  py: 0.75,
  px: 1.5,
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  whiteSpace: "nowrap",
  ...(align === "center" && { textAlign: "center" }),
});

const UnchangedStocks = ({
  stock,
  tableHeader,
  showUnchangedStocks,
  setShowUnchangedStocks,
}) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("symbol");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClose = () => setShowUnchangedStocks(false);

  useEffect(() => {
    if (stock) {
      const formattedData = Object.entries(stock).map(
        ([keyString, valueObject]) => {
          const valueData = valueObject;
          const keyData = JSON.parse(keyString);

          return {
            symbol: keyData.symbol || "",
            currentPosition: valueData.currentPosition || "",
            signal: valueData.signal || "",
            reason: valueData.reason || 0,
          };
        }
      );

      const filteredUsers = applySortFilter(
        formattedData,
        getComparator(order, orderBy)
      );

      setData(filteredUsers);
    }
  }, [order, orderBy, stock]);

  return (
    <Dialog
      open={showUnchangedStocks}
      onClose={handleClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: 560,
          maxWidth: "95vw",
          height: "auto",
          maxHeight: "90vh",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            color: theme.palette.text.primary,
          }}
        >
          Unchanged Positions
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 2 }}>
        <TableContainer
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            maxHeight: "60vh",
            overflow: "auto",
          }}
        >
          <Table
            size="small"
            stickyHeader
            aria-label="unchanged stocks table"
          >
            <VantageListHead
              order={order}
              orderBy={orderBy}
              headLabel={tableHeader}
              noStatus={true}
              customizeFont={12}
              rowCount={data.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {data?.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Tablecell align="left" sx={cellSx(theme, "left")}>
                    {row?.symbol?.toUpperCase() || ""}
                  </Tablecell>
                  <Tablecell align="center" sx={cellSx(theme, "center")}>
                    {row?.currentPosition !== "" &&
                    row?.currentPosition !== undefined &&
                    row?.currentPosition !== null
                      ? Number(row.currentPosition).toFixed(2)
                      : ""}
                  </Tablecell>
                  <Tablecell align="center" sx={cellSx(theme, "center")}>
                    {row?.signal !== undefined &&
                    row?.signal !== null &&
                    row?.signal !== ""
                      ? Number(row.signal).toFixed(2)
                      : "0.00"}
                  </Tablecell>
                  <Tablecell
                    align="left"
                    sx={{
                      ...cellSx(theme, "left"),
                      whiteSpace: "normal",
                      minWidth: 220,
                    }}
                  >
                    {row.reason == 0
                      ? "Within Individual Drift Threshold"
                      : "Skipped due to Delta Rebalance"}
                  </Tablecell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default UnchangedStocks;
